import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/** Valoarea e de obicei un id numeric; poate fi si text, ex. un SKU. */
export interface SearchSelectOption<V extends string | number = number> {
  value: V
  label: string
}

interface SearchSelectProps<V extends string | number> {
  options: SearchSelectOption<V>[]
  value: V | null
  onChange: (value: V | null) => void
  placeholder?: string
  disabled?: boolean
}

/** Cât spațiu îi trebuie listei ca să merite deschisă în jos, altfel se deschide în sus. */
const MIN_SPATIU = 200
const MAX_INALTIME = 288

interface Pozitie {
  left: number
  width: number
  top?: number
  bottom?: number
  maxHeight: number
}

/**
 * Dropdown cu câmp de căutare (typeahead). Fără dependențe externe.
 *
 * Lista se randează în `document.body`, nu lângă buton. Ca element `absolute` înăuntru,
 * o tăia orice părinte cu `overflow` — în fereastra de import sell-out, care are
 * `overflow-y-auto`, din toți clienții se vedea unul singur (14.09.2026). Același lucru
 * se întâmpla în antetul fix al paginilor.
 */
export function SearchSelect<V extends string | number = number>({
  options,
  value,
  onChange,
  placeholder = '— alege —',
  disabled,
}: SearchSelectProps<V>) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [poz, setPoz] = useState<Pozitie | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const listaRef = useRef<HTMLDivElement>(null)
  const cautaRef = useRef<HTMLInputElement>(null)

  const selected = options.find((o) => o.value === value) ?? null

  // Click în afară: lista nu mai e copilul wrapper-ului, deci se verifică amândouă.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node
      if (wrapRef.current?.contains(t) || listaRef.current?.contains(t)) return
      setOpen(false)
      setQuery('')
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  // Poziția listei, în coordonate de ecran. Se recalculează la deschidere și la orice
  // derulare sau redimensionare — altfel lista ar rămâne agățată unde era butonul.
  useLayoutEffect(() => {
    if (!open) {
      setPoz(null)
      return
    }
    const aseaza = () => {
      const r = wrapRef.current?.getBoundingClientRect()
      if (!r) return
      const subButon = window.innerHeight - r.bottom - 8
      const pesteButon = r.top - 8
      // În jos, dacă încape rezonabil; altfel în sus, unde e loc mai mult.
      const inJos = subButon >= MIN_SPATIU || subButon >= pesteButon
      setPoz({
        left: r.left,
        width: r.width,
        ...(inJos ? { top: r.bottom + 4 } : { bottom: window.innerHeight - r.top + 4 }),
        maxHeight: Math.min(MAX_INALTIME, Math.max(inJos ? subButon : pesteButon, 120)),
      })
    }
    aseaza()
    // `true` = și derulările din containere interioare, nu doar cea a ferestrei.
    window.addEventListener('scroll', aseaza, true)
    window.addEventListener('resize', aseaza)
    return () => {
      window.removeEventListener('scroll', aseaza, true)
      window.removeEventListener('resize', aseaza)
    }
  }, [open])

  /*
    Cursorul sare în căutare la deschidere, ca să poți tasta direct — DOAR cu mouse.
    Pe touch, focusul ridică tastatura peste jumătate de ecran, exact când omul voia întâi
    să deruleze lista; de-asta nu era `autoFocus` deloc. `pointer: fine` separă cele două
    cazuri mai bine decât lățimea ecranului.
  */
  useEffect(() => {
    if (!poz) return
    if (!window.matchMedia?.('(pointer: fine)').matches) return
    cautaRef.current?.focus()
  }, [poz])

  const filtered = query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options

  const lista = poz && (
    <div
      ref={listaRef}
      style={{
        position: 'fixed',
        left: poz.left,
        width: poz.width,
        top: poz.top,
        bottom: poz.bottom,
        maxHeight: poz.maxHeight,
      }}
      className="z-[60] overflow-auto rounded border border-gray-200 bg-white shadow-lg"
    >
      <div className="sticky top-0 bg-white p-2">
        <input
          ref={cautaRef}
          className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
          placeholder="Caută…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {selected && (
        <button
          type="button"
          className="block w-full px-3 py-1.5 text-left text-sm text-gray-500 hover:bg-gray-50"
          onClick={() => {
            onChange(null)
            setOpen(false)
            setQuery('')
          }}
        >
          {placeholder}
        </button>
      )}
      {filtered.length === 0 ? (
        <div className="px-3 py-2 text-sm text-gray-400">Niciun rezultat.</div>
      ) : (
        filtered.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 ${
              o.value === value ? 'bg-b4l-blue/10 font-medium' : ''
            }`}
            onClick={() => {
              onChange(o.value)
              setOpen(false)
              setQuery('')
            }}
          >
            {o.label}
          </button>
        ))
      )}
    </div>
  )

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded-md border border-gray-400 bg-white px-3 py-2 text-left shadow-sm hover:border-gray-500 disabled:bg-gray-50 disabled:text-gray-400"
      >
        <span className={selected ? '' : 'text-gray-400'}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="ml-2 text-gray-400">▾</span>
      </button>

      {open && !disabled && lista && createPortal(lista, document.body)}
    </div>
  )
}
